import type { Message, MessageType } from '@/domain/message.types'
import {
	MESSAGE_TRANSLATE_BATCH_COMBINED_LIMIT,
	MESSAGE_TRANSLATE_BATCH_ITEMS_LIMIT,
	MESSAGE_TRANSLATE_TARGET_LOCALES_LIMIT,
	MessageTranslationStatus,
} from '@/domain/message.types'
import { isStuck } from '@/hooks/useMessages'
import { buildRequestKey, parseRequestKey } from './request-key'

/**
 * Pure model behind the Message resolution matrix: rows are translation groups,
 * columns are locales, cells are one group's row for one locale (or the absence
 * of one). Nothing here fetches or holds state, so every rule below is unit
 * testable on plain objects.
 */

/**
 * Where a cell's text came from. Two states only: a person wrote it, or a machine
 * did. That is the whole question this grid answers about existing copy, and any
 * finer split (source row versus hand-authored locale row) is a distinction a
 * reader cannot act on.
 */
export enum MatrixProvenance {
	/** Written by a person — the group's source row, or a hand-authored locale row. */
	MANUAL = 'manual',
	/** Produced by an AI translation job. */
	AI = 'ai',
}

/** A cell's translation-job state, as far as the UI needs to distinguish. */
export enum MatrixJobState {
	NONE = 'none',
	PENDING = 'pending',
	TRANSLATING = 'translating',
	READY = 'ready',
	FAILED = 'failed',
	/** Non-terminal but sat unchanged past the shared stuck threshold — presumed dead, offer a retry. */
	STUCK = 'stuck',
}

export interface MatrixCell {
	locale: string
	/** Null when the group has no row for this locale — an empty, tickable gap. */
	message: Message | null
	provenance: MatrixProvenance | null
	jobState: MatrixJobState
	/** True for the row's own source locale, which can never be an AI-translation target. */
	isSourceLocale: boolean
}

export interface MatrixRow {
	/** The group's `isSource` row id — the ONLY id the translate endpoints accept as a source. */
	sourceMessageId: string
	translationGroupId: string
	title: string
	messageType: MessageType
	position?: string | null
	sourceLocale: string
	/** One cell per locale column, in the same order as the columns. */
	cells: MatrixCell[]
	/** The group's variants have not arrived yet; only the source cell is known. */
	isLoading: boolean
}

const JOB_STATE_BY_STATUS: Record<MessageTranslationStatus, MatrixJobState> = {
	[MessageTranslationStatus.PENDING]: MatrixJobState.PENDING,
	[MessageTranslationStatus.TRANSLATING]: MatrixJobState.TRANSLATING,
	[MessageTranslationStatus.READY]: MatrixJobState.READY,
	[MessageTranslationStatus.FAILED]: MatrixJobState.FAILED,
}

/** A translation job is stamped on the row it produced, so its presence is what separates generated copy from written copy. */
export const provenanceOf = (message: Message): MatrixProvenance =>
	message.translationStatus ? MatrixProvenance.AI : MatrixProvenance.MANUAL

/** Reuses `isStuck` rather than re-deriving the threshold, so the matrix and the detail page agree on what "presumed dead" means. */
export const jobStateOf = (message: Message): MatrixJobState => {
	const status = message.translationStatus
	if (!status) return MatrixJobState.NONE
	if (isStuck(status, message.updatedAt)) return MatrixJobState.STUCK
	return JOB_STATE_BY_STATUS[status]
}

/**
 * The matrix's columns: every locale any listed group covers, plus any locale
 * the user added as an empty column. Source locales lead, because they are the
 * copy everything else derives from; the rest follow alphabetically.
 */
export function buildLocaleColumns(variants: readonly Message[], addedLocales: readonly string[]): string[] {
	const sourceLocales = new Set(variants.filter((v) => v.isSource).map((v) => v.languageIso))
	const all = new Set([...variants.map((v) => v.languageIso), ...addedLocales])
	const rest = [...all].filter((locale) => !sourceLocales.has(locale)).sort()
	return [...[...sourceLocales].sort(), ...rest]
}

/** One row of the matrix: the group's source metadata plus a cell per column. */
export function buildRow(
	source: Message,
	variants: readonly Message[],
	localeColumns: readonly string[],
	isLoading: boolean,
): MatrixRow {
	const byLocale = new Map(variants.map((v) => [v.languageIso, v]))
	return {
		sourceMessageId: source.messageId,
		translationGroupId: source.translationGroupId,
		title: source.title,
		messageType: source.messageType,
		position: source.position,
		sourceLocale: source.languageIso,
		isLoading,
		cells: localeColumns.map((locale) => {
			const message = byLocale.get(locale) ?? null
			return {
				locale,
				message,
				provenance: message ? provenanceOf(message) : null,
				jobState: message ? jobStateOf(message) : MatrixJobState.NONE,
				isSourceLocale: locale === source.languageIso,
			}
		}),
	}
}

/** A running job owns its cell and writes to it when it finishes, so nothing else may write there meanwhile. */
export const isCellInFlight = (cell: MatrixCell): boolean =>
	cell.jobState === MatrixJobState.PENDING || cell.jobState === MatrixJobState.TRANSLATING

/**
 * A cell whose job is still running must not be re-queued, and the source locale
 * is never a target. A row whose group has not arrived yet offers no target at
 * all: every non-source cell reads `message: null` until it does, so ticking one
 * would queue a translation over copy nobody has seen.
 */
export const isCellSelectable = (cell: MatrixCell, isRowLoading: boolean): boolean =>
	!isRowLoading && !cell.isSourceLocale && !isCellInFlight(cell)

/** Every cell one row could have translated: the exact set a row-level tick reaches. */
export const selectableKeysInRow = (row: MatrixRow): string[] =>
	row.cells
		.filter((cell) => isCellSelectable(cell, row.isLoading))
		.map((cell) => buildRequestKey(row.sourceMessageId, cell.locale))

/**
 * Every cell one locale column could have translated. Rows whose own source
 * locale IS this column are skipped, so a column tick never tries to translate a
 * piece of copy into the language it was written in.
 */
export const selectableKeysInColumn = (rows: readonly MatrixRow[], locale: string): string[] =>
	rows
		.filter((row) => row.cells.some((cell) => cell.locale === locale && isCellSelectable(cell, row.isLoading)))
		.map((row) => buildRequestKey(row.sourceMessageId, locale))

/**
 * The ticked keys that still name a tickable cell in the rows on screen. Ticks
 * are held by `(sourceMessageId, locale)` and survive a refetch, which is the
 * point - but they also survive a filter change that drops the row entirely, or
 * a cell going `PENDING` by another route. What is not on screen is not part of
 * the count the button names, nor of the batch it submits.
 */
export function liveSelection(rows: readonly MatrixRow[], selection: ReadonlySet<string>): Set<string> {
	const tickable = new Set(rows.flatMap(selectableKeysInRow))
	return new Set([...selection].filter((key) => tickable.has(key)))
}

/**
 * A row in edit mode shows textareas rather than tick boxes, so neither a row nor
 * a column tick may reach it: a whole-axis tick reaches exactly the cells that
 * display a box.
 */
export const tickableRows = (rows: readonly MatrixRow[], editingRowId: string | null): MatrixRow[] =>
	rows.filter((row) => row.sourceMessageId !== editingRowId)

/** How much of a row or column is ticked, as a tri-state checkbox reads it. */
export enum BulkSelectState {
	NONE = 'none',
	SOME = 'some',
	ALL = 'all',
}

export function bulkSelectState(keys: readonly string[], selection: ReadonlySet<string>): BulkSelectState {
	const ticked = keys.filter((key) => selection.has(key)).length
	if (ticked === 0) return BulkSelectState.NONE
	return ticked === keys.length ? BulkSelectState.ALL : BulkSelectState.SOME
}

/** Ticked cell keys → the batch endpoint's shape, one item per source message. */
export function selectionToBatchItems(
	selection: ReadonlySet<string>,
): { messageId: string; targetLocales: string[] }[] {
	const byMessage = new Map<string, string[]>()
	for (const key of selection) {
		const { messageId, locale } = parseRequestKey(key)
		if (!messageId || !locale) continue
		const locales = byMessage.get(messageId)
		if (locales) locales.push(locale)
		else byMessage.set(messageId, [locale])
	}
	return [...byMessage].map(([messageId, targetLocales]) => ({ messageId, targetLocales: targetLocales.sort() }))
}

/**
 * The client-side half of `messageRequestTranslationBatchSchema`'s caps. Checked
 * before sending so an over-large tick set is explained here rather than coming
 * back as an opaque 422.
 */
export function validateBatchLimits(items: readonly { targetLocales: string[] }[]): string | null {
	if (items.length === 0) return null
	if (items.length > MESSAGE_TRANSLATE_BATCH_ITEMS_LIMIT)
		return `Select cells from at most ${MESSAGE_TRANSLATE_BATCH_ITEMS_LIMIT} messages for one bulk translation.`
	if (items.some((item) => item.targetLocales.length > MESSAGE_TRANSLATE_TARGET_LOCALES_LIMIT))
		return `Select at most ${MESSAGE_TRANSLATE_TARGET_LOCALES_LIMIT} locales for any one message.`
	const combined = items.reduce((sum, item) => sum + item.targetLocales.length, 0)
	if (combined > MESSAGE_TRANSLATE_BATCH_COMBINED_LIMIT)
		return `Select at most ${MESSAGE_TRANSLATE_BATCH_COMBINED_LIMIT} cells in total for one bulk translation.`
	return null
}

/** How many ticked cells already hold text an AI translation would replace — the number the confirmation names. */
export function countOverwrites(rows: readonly MatrixRow[], selection: ReadonlySet<string>): number {
	return rows.reduce(
		(total, row) =>
			total +
			row.cells.filter(
				(cell) =>
					selection.has(buildRequestKey(row.sourceMessageId, cell.locale)) &&
					(cell.message?.content.trim().length ?? 0) > 0,
			).length,
		0,
	)
}
